import { Option } from '@sapphire/result';
import { BaseMapper } from '@ps/utils';
import { CompanyDetailResponseDto } from '../dtos/company.dto';
import {
  CompanyDetail,
  CompanyNetsuiteDetail,
} from '../../models/company.model';
import { OperatorUserMapper } from './operator-user.mapper';

const EMPTY_NETSUITE: CompanyNetsuiteDetail = {
  enabled: false,
  internalId: '',
  companyName: '',
  primaryEmail: '',
  billingAddress: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: '',
};

type BuyerFields = Pick<
  CompanyDetail,
  | 'isAServiceAccount'
  | 'isTestAccount'
  | 'generateInvoice'
  | 'tempGenerateInvoice'
  | 'accountManagers'
  | 'projectManager'
  | 'orgId'
>;

type SupplierUrlFields = Pick<
  CompanyDetail,
  | 'suppUrlCompletes'
  | 'suppUrlOverquota'
  | 'suppUrlTerms'
  | 'suppUrlSecurityTerms'
>;

type SupplierFlagFields = Pick<
  CompanyDetail,
  | 'isNotify'
  | 'isExpose'
  | 'isAdditionalMetrics'
  | 'isPureScoreEnable'
  | 'isRdFlagsEnable'
  | 'appendProfileOnRedirects'
  | 'isIncludeBuyerInfoOnStatementReport'
  | 'isManualSupplier'
  | 'showTrafficChannel'
>;

type NetsuiteBillingFields = Pick<
  CompanyNetsuiteDetail,
  | 'billingAddress'
  | 'billingCity'
  | 'billingState'
  | 'billingZip'
  | 'billingCountry'
>;

export class CompanyDetailMapper extends BaseMapper<
  CompanyDetailResponseDto,
  CompanyDetail
> {
  private readonly _userMapper = new OperatorUserMapper();

  public fromDto(dto: CompanyDetailResponseDto): CompanyDetail {
    return {
      id: dto.id,
      name: dto.name ?? '',
      address: dto.address ?? '',
      phone: dto.phone ?? '',
      primaryLiaison: dto.primaryLiaison ?? '',
      secondaryLiaison: dto.secondaryLiaison ?? '',
      fx: dto.fx ?? null,
      isABuyer: dto.isABuyer ?? false,
      isASupplier: dto.isASupplier ?? false,
      isAnOperator: dto.isAnOperator ?? false,
      isFusion: dto.isFusion ?? false,
      nsInternalId: dto.nsInternalId ?? null,
      netsuite: this._mapNetsuite(dto),
      ...this._mapBuyerFields(dto),
      ...this._mapSupplierUrlFields(dto),
      ...this._mapSupplierFlagFields(dto),
    };
  }

  private _mapNetsuiteBilling(
    dto: CompanyDetailResponseDto,
  ): NetsuiteBillingFields {
    const billing = dto.netsuite?.billingInfo;
    return {
      billingAddress: billing?.address ?? '',
      billingCity: billing?.city ?? '',
      billingState: billing?.state ?? '',
      billingZip: billing?.zip ?? '',
      billingCountry: billing?.country ?? '',
    };
  }

  private _mapNetsuite(dto: CompanyDetailResponseDto): CompanyNetsuiteDetail {
    if (!dto.netsuite) return EMPTY_NETSUITE;
    return {
      enabled: dto.netsuite.enabled ?? false,
      internalId: dto.netsuite.internalId ?? '',
      companyName: dto.netsuite.companyName ?? '',
      primaryEmail: dto.netsuite.primaryEmail ?? '',
      ...this._mapNetsuiteBilling(dto),
    };
  }

  private _mapBuyerFields(dto: CompanyDetailResponseDto): BuyerFields {
    return {
      isAServiceAccount: dto.isAServiceAccount ?? false,
      isTestAccount: dto.isTestAccount ?? false,
      generateInvoice: dto.generateInvoice ?? '',
      tempGenerateInvoice: dto.tempGenerateInvoice ?? '',
      accountManagers: Option.from(dto.accountManagers)
        .map((users) => this._userMapper.fromDtoList(users))
        .unwrapOr([]),
      projectManager: Option.from(dto.projectManager)
        .filter(
          (projectManager): projectManager is { id: number; name: string } =>
            projectManager.id != null && projectManager.name != null,
        )
        .map((projectManager) => this._userMapper.fromDto(projectManager))
        .unwrapOr(null),
      orgId: dto.orgId ?? '',
    };
  }

  private _mapSupplierUrlFields(
    dto: CompanyDetailResponseDto,
  ): SupplierUrlFields {
    return {
      suppUrlCompletes: dto.supp_url_completes ?? '',
      suppUrlOverquota: dto.supp_url_overquota ?? '',
      suppUrlTerms: dto.supp_url_terms ?? '',
      suppUrlSecurityTerms: dto.supp_url_security_terms ?? '',
    };
  }

  private _mapSupplierFlagFields(
    dto: CompanyDetailResponseDto,
  ): SupplierFlagFields {
    return {
      isNotify: dto.isNotify ?? false,
      isExpose: dto.isExpose ?? false,
      isAdditionalMetrics: dto.isAdditionalMetrics ?? false,
      isPureScoreEnable: dto.isPureScoreEnable ?? false,
      isRdFlagsEnable: dto.isRdFlagsEnable ?? false,
      appendProfileOnRedirects: dto.appendProfileOnRedirects ?? false,
      isIncludeBuyerInfoOnStatementReport:
        dto.isIncludeBuyerInfoOnStatementReport ?? false,
      isManualSupplier: dto.isManualSupplier ?? false,
      showTrafficChannel: dto.showTrafficChannel ?? false,
    };
  }
}
